import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usersService } from "../features/users/service";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

interface VerificationRequiredModalProps {
  /** The server's dm:error / 403 message, shown verbatim above the CTA. */
  message: string;
  onClose: () => void;
}

type MissingField = { key: string; label: string; step_key: string };
type Status =
  | "idle" | "loading" | "requested" | "already_requested"
  | "incomplete_profile" | "pending_profile_completion" | "error";

/**
 * Shown whenever a seller/buyer is blocked by is_verified=false (a DM
 * websocket dm:error with code "NOT_VERIFIED", or a REST 403 with code
 * SELLER_NOT_VERIFIED / BUYER_NOT_VERIFIED). Gives them a "Request
 * Verification" action instead of leaving them stuck with only a generic
 * error and no next step — calls POST /api/v1/profile/request-verification,
 * which is rate-limited to once/24h server-side.
 *
 * Sellers whose business profile (GST/company details, office location,
 * team size, years in business) is still incomplete get a checklist of
 * what's missing instead of a plain error, a way to jump straight to it, and
 * the choice of having verification requested automatically the moment
 * they finish (vs. filling it in and asking again manually, vs. not now).
 */
const VerificationRequiredModal: React.FC<VerificationRequiredModalProps> = ({ message, onClose }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("idle");
  const [resultMessage, setResultMessage] = useState<string>("");
  const [missingFields, setMissingFields] = useState<MissingField[]>([]);

  useBodyScrollLock();

  const handleRequest = async () => {
    setStatus("loading");
    try {
      const res = await usersService.requestVerification();
      setStatus(res.status);
      setResultMessage(res.message);
      setMissingFields(res.missing_fields ?? []);
    } catch (err: any) {
      setStatus("error");
      setResultMessage(
        err?.response?.data?.detail || "Couldn't send the request. Please try again in a moment.",
      );
    }
  };

  const goToProfile = () => {
    const stepKey = missingFields[0]?.step_key;
    onClose();
    navigate(stepKey ? `/profile?step=${encodeURIComponent(stepKey)}` : "/profile");
  };

  const confirmAutoSend = async () => {
    setStatus("loading");
    try {
      await usersService.requestVerification(true);
    } catch {
      // Non-fatal — the seller is heading to fill in the fields either way;
      // the manual "Request Verification" button still works once they're done.
    }
    goToProfile();
  };

  const done = status === "requested" || status === "already_requested";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl"
      >
        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
              <svg className="h-5 w-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <div className="min-w-0 pt-0.5">
              <h2 className="text-lg font-semibold leading-tight text-white">Verification required</h2>
              <p className="mt-1 text-sm text-gray-400">{message}</p>
            </div>
          </div>

          {done && (
            <p className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2.5 text-sm text-emerald-400">
              {resultMessage}
            </p>
          )}
          {status === "error" && (
            <p className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-3.5 py-2.5 text-sm text-red-400">
              {resultMessage}
            </p>
          )}

          {status === "incomplete_profile" && (
            <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
              <p className="text-sm font-semibold text-white">Update your profile with these fields first</p>
              <ul className="mt-2.5 space-y-1.5">
                {missingFields.map((f) => (
                  <li key={f.key} className="flex items-center gap-2 text-sm text-gray-300">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                    {f.label}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs leading-relaxed text-gray-500">
                Once these are filled in, your verification request can go out — either automatically the
                moment you finish, or you can send it yourself afterward.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  onClick={() => void confirmAutoSend()}
                  className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-500"
                >
                  Yes, send automatically →
                </button>
                <button
                  onClick={goToProfile}
                  className="w-full rounded-lg bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-200 transition-colors hover:bg-gray-700"
                >
                  I'll fill it in and request myself
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-800 p-4 sm:flex-row sm:justify-end sm:gap-3 sm:p-5">
          <button
            onClick={onClose}
            className="w-full rounded-xl bg-gray-800 px-4 py-2 text-sm text-gray-400 transition-colors hover:bg-gray-700 sm:w-auto"
          >
            {status === "incomplete_profile" ? "Not now" : "Close"}
          </button>
          {!done && status !== "incomplete_profile" && (
            <button
              onClick={handleRequest}
              disabled={status === "loading"}
              className="w-full rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-500 disabled:opacity-50 sm:w-auto"
            >
              {status === "loading" ? "Sending..." : "Request Verification"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationRequiredModal;
